import { useState, useEffect } from "react";
import axios from "axios";
import { Check, Shield, AlertTriangle } from "lucide-react";
import {
  RETTIGHEDER_FALLBACK,
  ADMIN_BASELINE_FALLBACK,
  OPT_IN_RETTIGHEDER,
  rettighederTilDict,
  vagtplanByer,
  VAGTPLAN_BYER,
  VAGTPLAN_BYER_KEY,
} from "@/lib/rettigheder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalisér en afdelingsbetegnelse til kanonisk form (matcher VAGTPLAN_BYER).
// Bl.a. "Koebenhavn"/"Kobenhavn" → "København".
const AFD_ALIAS = {
  koebenhavn: "København",
  kobenhavn: "København",
  copenhagen: "København",
  aarhus: "Aarhus",
  arhus: "Aarhus",
};
const normaliserAfdeling = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";
  const key = s.toLowerCase().replace(/ø/g, "oe").replace(/å/g, "a");
  if (AFD_ALIAS[key]) return AFD_ALIAS[key];
  // Match mod kanonisk liste (case-insensitivt), ellers behold som givet.
  const hit = VAGTPLAN_BYER.find(b => b.toLowerCase() === s.toLowerCase());
  return hit || s;
};

/**
 * Genbrugelig editor til individuelle rettigheder.
 *
 * Props:
 *  - rolle: "member" | "admin" | "superadmin" | "owner"
 *  - vaerdier: rå rettigheder (array eller dict) — initial
 *  - onChange(dict): kaldes når brugeren toggler
 *  - token: JWT (for at hente katalog fra /admin/rettigheder)
 *  - brugerNavn: visningsnavn (bruges i bekraeftelses-popup for fortrolige)
 *  - primaerAfdeling: den redigerede medarbejders egen afdeling. Altid krydset
 *    + låst i vagtplan-by-listen. "" = ingen primær (alle bokse frit valgbare).
 */
export default function RettighedsEditor({ rolle, vaerdier, onChange, token, brugerNavn, itemId, onRolleChange, primaerAfdeling = "" }) {
  const [katalog, setKatalog] = useState(RETTIGHEDER_FALLBACK);
  const [baseline, setBaseline] = useState(ADMIN_BASELINE_FALLBACK);
  const [optIn, setOptIn] = useState(OPT_IN_RETTIGHEDER);
  const [loading, setLoading] = useState(true);
  const [dict, setDict] = useState(() => rettighederTilDict(vaerdier));
  // Tilladte byer for kan_se_vagtplan ([] = alle byer).
  const [byer, setByer] = useState(() => vagtplanByer(vaerdier));
  const [showConfirmFortrolige, setShowConfirmFortrolige] = useState(false);
  // #39.2: rolle-vælger øverst. owner behandles som superadmin i valget;
  // api_admin bevares som sig selv.
  const [rolleValg, setRolleValg] = useState(() => (rolle === "owner" ? "superadmin" : (rolle || "member")));
  const [rolleGemmer, setRolleGemmer] = useState(false);
  useEffect(() => { setRolleValg(rolle === "owner" ? "superadmin" : (rolle || "member")); }, [rolle]);

  useEffect(() => {
    axios.get(`${API}/admin/rettigheder`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (Array.isArray(r.data.rettigheder) && r.data.rettigheder.length) setKatalog(r.data.rettigheder);
        if (Array.isArray(r.data.admin_baseline) && r.data.admin_baseline.length) setBaseline(r.data.admin_baseline);
        if (Array.isArray(r.data.opt_in)) setOptIn(r.data.opt_in);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { setDict(rettighederTilDict(vaerdier)); setByer(vagtplanByer(vaerdier)); }, [vaerdier]);

  const optInSet = new Set(optIn);
  const erSuper = rolle === "superadmin" || rolle === "owner";
  const erApiAdmin = rolle === "api_admin";
  const erAdmin = rolle === "admin";

  // Send den fulde rettigheds-payload (booleans + by-parameter) til parent.
  const emit = (nyDict, nyByer) => {
    const payload = { ...nyDict };
    // Kun relevant når vagtplan er aktiv; ellers udelad parameteren.
    if (nyDict.kan_se_vagtplan && nyByer && nyByer.length) payload[VAGTPLAN_BYER_KEY] = nyByer;
    onChange && onChange(payload);
  };

  // Direkte toggle uden bekraeftelse — sætter ny værdi og notifierer parent.
  const bekraeftToggle = (id) => {
    const ny = { ...dict, [id]: !dict[id] };
    // #42.2: "Elev-stamkort (rediger)" kræver "(se)". Sæt se når rediger sættes;
    // fjern rediger når se fjernes.
    if (id === "kan_redigere_elever" && ny.kan_redigere_elever) ny.kan_se_elever = true;
    if (id === "kan_se_elever" && !ny.kan_se_elever) ny.kan_redigere_elever = false;
    setDict(ny);
    emit(ny, byer);
  };

  // Den redigerede medarbejders primær-afdeling (kanonisk form). Altid krydset
  // + låst når en SPECIFIK by-liste gemmes. "" => ingen primær.
  const primaerBy = normaliserAfdeling(primaerAfdeling);

  // Sørg for at primær altid er med i en specifik (ikke-tom) liste, og at
  // listen er renset for dubletter.
  const medPrimaer = (liste) => {
    const set = new Set(liste.filter(Boolean));
    if (primaerBy) set.add(primaerBy);
    return [...set];
  };

  // Sæt tilladte byer for vagtplan. Tom liste => "Alle afdelinger" (= alle byer,
  // ingen begrænsning). Ikke-tom => begræns, men behold altid primær.
  const setVagtplanByer = (nyByer) => {
    const næste = nyByer.length === 0 ? [] : medPrimaer(nyByer);
    setByer(næste);
    emit(dict, næste);
  };

  // Toggle en enkelt afdeling. Primær kan ikke fjernes. Hvis vi står på "Alle
  // afdelinger" (tom liste) og afkrydser en by, starter vi en specifik liste
  // (primær + den valgte by). Klik på en by fjerner "Alle afdelinger" automatisk.
  const toggleBy = (by) => {
    if (by === primaerBy) return; // primær er låst
    // Hvis "Alle afdelinger" er aktiv, start en specifik liste med primær + valgte by.
    if (byer.length === 0) {
      setVagtplanByer(medPrimaer([by]));
      return;
    }
    const næste = byer.includes(by) ? byer.filter(b => b !== by) : [...byer, by];
    setVagtplanByer(næste);
  };

  // "Alle afdelinger": til => tom liste (fuld adgang). fra => specifik liste.
  // Hvis ingen primær, brug første tilgængelige by så listen ikke er tom (og
  // alleByer ikke sætter sig selv tilbage).
  const setAlleAfdelinger = (paa) => {
    if (paa) {
      setVagtplanByer([]);
    } else {
      const fravalg = medPrimaer([]);
      // Sørg for at listen er ikke-tom, ellers forbliver alleByer = true.
      setVagtplanByer(fravalg.length > 0 ? fravalg : [VAGTPLAN_BYER[0]]);
    }
  };

  const toggle = (id) => {
    const erFortrolige = id === "kan_se_fortrolige_medarbejderoplysninger";
    // Vis bekræftelses-popup når admin er ved at AKTIVERE fortrolige-rettigheden.
    if (erFortrolige && erAdmin && !dict[id]) {
      setShowConfirmFortrolige(true);
      return;
    }
    bekraeftToggle(id);
  };

  // #39.2: skift rolle. Persisteres straks via medarbejder-rolle-endpoint
  // (kræver itemId). Opdaterer rolle-badge i både Medarbejdere- og Brugere-liste.
  const ROLLE_VALG = [
    { v: "member", label: "Medarbejder" },
    { v: "light", label: "Medarbejder (light)" },
    { v: "admin", label: "Admin" },
    { v: "superadmin", label: "SuperAdmin" },
    { v: "api_admin", label: "API admin (skrivebeskyttet)" },
  ];
  const vaelgRolle = async (ny) => {
    if (ny === rolleValg) return;
    setRolleValg(ny);
    if (!itemId) { onRolleChange && onRolleChange(ny); return; }
    setRolleGemmer(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${itemId}/rolle`, { rolle: ny },
        { headers: { Authorization: `Bearer ${token}` } });
      onRolleChange && onRolleChange(ny);
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke ændre rolle");
      setRolleValg(rolle === "owner" ? "superadmin" : (rolle || "member"))  // api_admin bevares som sig selv;
    } finally {
      setRolleGemmer(false);
    }
  };

  const RolleVaelger = (
    <div className="rounded-xl border border-border bg-secondary/30 px-4 py-3">
      <p className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        Rolle {rolleGemmer && <span className="text-muted-foreground/60 normal-case font-normal">· gemmer…</span>}
      </p>
      <div className="flex flex-col gap-1.5">
        {ROLLE_VALG.map(r => (
          <label key={r.v} className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="radio" name={`rolle-${itemId || "x"}`} checked={rolleValg === r.v}
              onChange={() => vaelgRolle(r.v)} disabled={rolleGemmer}
              className="accent-[#FF2A6D]"
            />
            <span className="text-sm">{r.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  if (loading) return <div className="text-[13px] text-muted-foreground italic">Henter rettigheder...</div>;

  if (erSuper) {
    return (
      <div className="flex flex-col gap-3">
        {RolleVaelger}
        <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 px-4 py-3">
          <p className="text-sm font-semibold text-violet-700 dark:text-violet-400 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Fuld adgang
          </p>
          <p className="text-[13px] text-muted-foreground mt-1">Superadmins har alle rettigheder. Ingen yderligere kan tildeles.</p>
        </div>
      </div>
    );
  }

  if (erApiAdmin) {
    return (
      <div className="flex flex-col gap-3">
        {RolleVaelger}
        <div className="rounded-xl border border-slate-400/30 bg-slate-500/5 px-4 py-3">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Skrivebeskyttet adgang
          </p>
          <p className="text-[13px] text-muted-foreground mt-1">
            API admin ser alt som SuperAdmin, men kan ikke ændre noget — alle handlinger
            der gemmer, opretter eller sletter afvises. Ingen yderligere rettigheder kan tildeles.
          </p>
        </div>
      </div>
    );
  }

  const baselineSet = new Set(baseline);

  // For members: skjul opt-in-rettigheder helt (de kan aldrig få fortrolige).
  // For admins: vis dem som ekstra (ikke baseline).
  const synligeRettigheder = katalog.filter(r => {
    if (!erAdmin && optInSet.has(r.id)) return false;
    return true;
  });

  return (
    <div className="flex flex-col gap-3">
      {RolleVaelger}
      {erAdmin && (
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Default-adgang for Admin</p>
          <div className="flex flex-col gap-1.5 mb-3">
            {synligeRettigheder.filter(r => baselineSet.has(r.id)).map(r => (
              <div key={r.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 opacity-70 cursor-default" title={r.beskrivelse}>
                <span className="w-4 h-4 rounded border bg-[#FF2A6D]/40 border-[#FF2A6D]/40 flex items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5 text-white" />
                </span>
                <span className="text-sm">{r.label}</span>
              </div>
            ))}
          </div>
          <p className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Ekstra rettigheder</p>
        </div>
      )}
      {!erAdmin && (
        <p className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Individuelle rettigheder</p>
      )}
      <div className="flex flex-col gap-1.5">
        {synligeRettigheder.filter(r => !erAdmin || !baselineSet.has(r.id)).map(r => {
          const checked = !!dict[r.id];
          const erFortrolige = r.id === "kan_se_fortrolige_medarbejderoplysninger";
          const erVagtplan = r.id === "kan_se_vagtplan";
          const alleByer = byer.length === 0;
          return (
            <div key={r.id}>
              <label className={`flex items-center gap-3 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-secondary/50 ${erFortrolige ? "border border-amber-500/30 bg-amber-500/5" : ""}`} title={r.beskrivelse}>
                <span
                  onClick={() => toggle(r.id)}
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${checked ? (erFortrolige ? "bg-amber-500 border-amber-500" : "bg-[#FF2A6D] border-[#FF2A6D]") : "border-border"}`}>
                  {checked && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                <span className="text-sm flex items-center gap-1.5 shrink-0" onClick={() => toggle(r.id)}>
                  {erFortrolige && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                  {r.label}
                </span>
                <span className="text-[13px] text-muted-foreground hidden md:block text-right leading-snug ml-auto max-w-[55%] break-words">{r.beskrivelse}</span>
              </label>

              {/* Adgang til afdelinger for Vagtplan-rettigheden — kun når aktiv.
                  Tom by-liste = "Alle afdelinger" (fuld adgang, ingen filter). */}
              {erVagtplan && checked && (
                <div className="ml-7 mt-1 mb-1 rounded-lg border border-border bg-secondary/30 px-3 py-2">
                  <p className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">↳ Adgang til afdelinger:</p>
                  <div className="flex flex-col gap-0.5">
                    {VAGTPLAN_BYER.map(by => {
                      const erPrimaer = !!primaerBy && by === primaerBy;
                      // Primær er altid krydset og låst. Individuelle byer er
                      // klikbare selv når "Alle afdelinger" er valgt — klik fjerner
                      // "Alle afdelinger" automatisk og starter en specifik liste.
                      const valgt = erPrimaer || alleByer || byer.includes(by);
                      const laast = erPrimaer;
                      return (
                        <label key={by}
                          className={`flex items-center gap-2 py-0.5 ${laast ? "cursor-default" : "cursor-pointer"}`}
                          title={erPrimaer ? "Primær afdeling — kan ikke fjernes" : `Adgang til ${by}`}>
                          <span onClick={() => { if (!laast) toggleBy(by); }}
                            className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${valgt ? (erPrimaer ? "bg-[#FF2A6D]/40 border-[#FF2A6D]/40" : "bg-[#FF2A6D] border-[#FF2A6D]") : "border-border"}`}>
                            {valgt && <Check className="w-2.5 h-2.5 text-white" />}
                          </span>
                          <span className="text-[13px]" onClick={() => { if (!laast) toggleBy(by); }}>
                            {by}{erPrimaer && <span className="text-muted-foreground"> (primær)</span>}
                          </span>
                        </label>
                      );
                    })}
                    {/* Genvej: fuld adgang som admin (ingen by-filtrering). */}
                    <label className="flex items-center gap-2 cursor-pointer py-0.5 mt-1 pt-1 border-t border-border/60" title="Fuld adgang til alle afdelinger (ingen begrænsning)">
                      <span onClick={() => setAlleAfdelinger(!alleByer)}
                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${alleByer ? "bg-[#FF2A6D] border-[#FF2A6D]" : "border-border"}`}>
                        {alleByer && <Check className="w-2.5 h-2.5 text-white" />}
                      </span>
                      <span className="text-[13px] font-medium" onClick={() => setAlleAfdelinger(!alleByer)}>Alle afdelinger</span>
                    </label>
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-1.5">
                    {alleByer ? "Fuld adgang til alle afdelinger." : `Begrænset til: ${byer.join(", ")}.`}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showConfirmFortrolige && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/10 backdrop-blur-[2px] p-4"
          onClick={() => setShowConfirmFortrolige(false)}
        >
          <div
            className="bg-background border border-amber-500/40 rounded-2xl shadow-2xl w-full max-w-md p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-base font-bold mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Du er ved at give adgang til fortrolige medarbejderoplysninger
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Denne adgang inkluderer løn, pension, kontrakter, feriebank og personlige
              bankoplysninger for ALLE medarbejdere i systemet.
            </p>
            <p className="text-sm font-medium mb-5">
              <span className="font-bold">{brugerNavn || "Denne admin"}</span> vil kunne se og redigere disse oplysninger.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirmFortrolige(false)}
                className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary"
              >
                Annullér
              </button>
              <button
                onClick={() => {
                  bekraeftToggle("kan_se_fortrolige_medarbejderoplysninger");
                  setShowConfirmFortrolige(false);
                }}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500 text-white hover:opacity-90"
              >
                Ja, giv adgang
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
